import { useEffect, useMemo } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { useAxios } from '@/hooks/useAxios';
import { useMy } from '@/hooks/useMy';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import { useWsLiveness, type WebSocketMessage } from '@/hooks/useWebSocket';
import {
  ChatMessageRequest,
  Conversation,
  ConversationResponse,
  TraceEvent,
} from '@/types';
import { useMutation } from '@/utils/api';

// Unconditional safety-net poll interval. This is wired as a plain
// `refetchInterval`, so it runs every 60s whenever the query has a mounted,
// focused observer — connected OR not. It is NOT gated on WS disconnect: its
// job is to cover a silently-dropped WS connection (a socket that looks alive
// but has stopped delivering events), not just an explicit disconnect.
// ENG-4506 adds one TIGHTENING (never a loosening) of this floor: when the
// socket has never connected at all (no VITE_WS_URL, or the WS backend never
// came up — useWsLiveness), the by-id query polls at 10s so status flips
// (active→completed, the Stop/Send gate) meet the ≤10s no-WS contract. The
// gate is the hasEverConnected LATCH, which a zombie socket satisfies and a
// reconnect flicker cannot trip — so the unconditional-60s philosophy above
// still covers every connected-then-broken case.
export const FALLBACK_POLL_MS = 60_000;
// ENG-4506: by-id status cadence while the WS has never been up (see above).
const NO_WS_STATUS_POLL_MS = 10_000;

// Coalesce WS-driven invalidations (status flips write ~3+ events per turn),
// but bound the coalescing: without maxWait, a sustained sub-2s event cadence
// resets the trailing debounce forever and the refetch starves until the
// fallback poll (ENG-4399).
export const INVALIDATE_DEBOUNCE_MS = 2_000;
export const INVALIDATE_MAX_WAIT_MS = 5_000;

export function useCreateConversation() {
  const axios = useAxios();
  const { invalidate } = useMy({ resource: 'conversation' });

  return useMutation({
    defaultErrorMessage: 'Failed to create conversation',
    errorByStatusCode: {
      503: 'Planner temporarily unavailable. Please try again in a moment.',
    },
    mutationFn: async (
      request: string | ChatMessageRequest
    ): Promise<Conversation> => {
      const body =
        typeof request === 'string'
          ? { conversationId: '', message: request }
          : {
              conversationId: request.conversationId || '',
              message: request.message,
              mode: request.mode || 'query',
            };
      const response = await axios.post<ConversationResponse>('/planner', body);

      if (!response.data.conversation) {
        throw new Error('No conversation returned from server');
      }

      return response.data.conversation;
    },
    onSuccess: () => {
      invalidate();
    },
  });
}

export function useSendMessage() {
  const axios = useAxios();

  return useMutation({
    defaultErrorMessage: 'Failed to send message',
    errorByStatusCode: {
      503: 'Planner temporarily unavailable. Please try again in a moment.',
    },
    mutationFn: async (request: ChatMessageRequest): Promise<string> => {
      const response = await axios.post<ConversationResponse>('/planner', {
        conversationId: request.conversationId,
        message: request.message,
        mode: request.mode || 'query',
      });

      if (!response.data.response?.success) {
        throw new Error('Message failed to send');
      }

      return response.data.response.response;
    },
    // No onSuccess invalidate: consumers of message/conversation queries drive
    // updates from WebSocket events via useRealtimeSubscription instead of
    // refetching on every mutation.
  });
}

export function useDeleteConversation() {
  const axios = useAxios();
  const { invalidate } = useMy({ resource: 'conversation' });

  return useMutation({
    defaultErrorMessage: 'Failed to delete conversation',
    errorByStatusCode: {
      503: 'Planner temporarily unavailable. Please try again in a moment.',
    },
    mutationFn: async (conversationId: string): Promise<void> => {
      await axios.delete('/planner', {
        data: { conversationId },
      });
    },
    onSuccess: () => {
      invalidate();
    },
  });
}

export function useGetConversations(opts?: {
  // Called synchronously with `message.key` when a `#conversation#` REMOVE
  // event arrives on the list subscription. Optional so callers that don't
  // render the sidebar overlay can omit it — the sidebar-overlay consumer
  // (useChatWorker) injects it to evict a just-created overlay row the
  // moment a remote delete lands, rather than waiting out its 90s TTL.
  onConversationRemoved?: (key: string) => void;
}) {
  const { data, invalidate, ...rest } = useMy(
    {
      resource: 'conversation',
      query: 'parent_id:self',
    },
    {
      refetchInterval: FALLBACK_POLL_MS,
      // Restore the old chatWorkerLiveness refocus-resync for the sidebar
      // list: the global 5-min staleTime gates TanStack's default focus
      // refetch, so within that window a tab refocused after a
      // silently-dropped WS would otherwise show stale rows until the next
      // 60s poll. 'always' bypasses the staleTime gate on focus for THIS
      // query only. The active conversation heals faster on its own
      // (exact-key WS sub + reconnect resync in useChatWorker), so the list
      // was the one surface where refocus staleness was user-visible.
      // NOTE: this covers only HALF of the old behavior — TanStack v5's
      // focusManager registers `visibilitychange` ONLY (query-core
      // focusManager.js), i.e. tab switches. The window-`focus` half
      // (alt-tab back to an already-visible window) is the scoped listener
      // in the effect below.
      refetchOnWindowFocus: 'always',
    }
  );

  // The other half of the old chatWorkerLiveness refocus-resync: it listened
  // to BOTH window `focus` and `visibilitychange`. The TanStack option above
  // handles visibility; this scoped listener handles the desktop case where
  // the browser window regains OS focus while the tab never changed
  // visibility. Scoped (not focusManager.setEventListener) on purpose: the
  // focusManager extension point is global and would re-enable v4-style
  // focus refetching for every query in the app. Immediate, not debounced —
  // refocus is a discrete user action, and TanStack dedupes concurrent
  // refetches of the same query.
  useEffect(() => {
    const onWindowFocus = () => invalidate();
    window.addEventListener('focus', onWindowFocus);
    return () => window.removeEventListener('focus', onWindowFocus);
  }, [invalidate]);

  const debouncedInvalidate = useDebouncedCallback(
    () => invalidate(),
    INVALIDATE_DEBOUNCE_MS,
    { maxWait: INVALIDATE_MAX_WAIT_MS }
  );

  useRealtimeSubscription({
    patterns: [{ pattern: '#conversation#', matchType: 'prefix' }],
    // Inspect the payload rather than discarding it: a REMOVE event must evict
    // a matching optimistic overlay row immediately (the local tombstone only
    // covers locally-initiated deletes, so a row deleted from another
    // tab/session or by a backend prune would otherwise ghost until TTL). The
    // debounced invalidate still runs REGARDLESS of eventName so the durable
    // list converges the same way it did before.
    onUpdate: (message: WebSocketMessage) => {
      if (message.eventName === 'REMOVE') {
        opts?.onConversationRemoved?.(message.key);
      }
      debouncedInvalidate();
    },
    // Subscribe rejected (e.g. MaxSubsPerConnection) → the sidebar list won't
    // self-heal via WS. Invalidate once so the 60s fallback poll isn't the only
    // recovery path; no resubscribe. Mirrors useGetConversationById (below).
    onError: reason => {
      console.error('useGetConversations: subscribe rejected', { reason });
      invalidate();
    },
  });

  // The server-side `parent_id:self` GSI query already restricts to ROOT
  // conversations (the parent gate). Only the hunt-iteration exclusion must be
  // applied client-side — there is no GSI for `huntId`. Hunt iterations are root
  // (parent_id === 'self') but carry a huntId and belong to the Hunts surface,
  // not the chat sidebar.
  //
  // Memoized on `data` so the returned array is reference-stable across renders
  // that don't change the query result — consumers use it in `useMemo` deps.
  // Filtering into a fresh copy also avoids mutating `useMy`'s memoized `data`
  // array in place (a plain `.sort()` would).
  const sortedConversations = useMemo(
    () =>
      (data || [])
        .filter((c: Conversation) => !c.huntId)
        .sort(
          (a: Conversation, b: Conversation) =>
            (new Date(b.created).getTime() || 0) -
            (new Date(a.created).getTime() || 0)
        ),
    [data]
  );

  return {
    data: sortedConversations as Conversation[],
    invalidate,
    ...rest,
  };
}

export function useGetChildConversations(
  parentId?: string,
  opts?: { user?: boolean }
) {
  // `user` scopes which DynamoDB partition the parent_id scan hits. Omitted =
  // the conversation-resource default (user partition `{account}:{user}`) —
  // the classic chat tree. `user: false` scans the tenant partition
  // (`{account}`), where hunt / HITL-Romulus subagent children live
  // (spawn_agent_sync.createSubagentConversation forces WithHunt/WithPublic ->
  // tenant scope). A parent_id scan resolves only within one partition, so a
  // caller that needs the whole subagent family must query BOTH (ENG-3976).
  const { data, invalidate, ...rest } = useMy(
    {
      resource: 'conversation',
      query: `parent_id:${parentId}`,
      ...(opts?.user !== undefined ? { user: opts.user } : {}),
    },
    {
      enabled: !!parentId,
      refetchInterval: FALLBACK_POLL_MS,
    }
  );

  const debouncedInvalidate = useDebouncedCallback(
    () => invalidate(),
    INVALIDATE_DEBOUNCE_MS,
    { maxWait: INVALIDATE_MAX_WAIT_MS }
  );

  // Match the fetch's partition: a `{ user: false }` tenant scan must ALSO
  // subscribe on the tenant partition, otherwise the subscription defaults to
  // user:true and a tenant subagent's conversation-INSERT never invalidates
  // this scan (it only self-heals on the 60s poll). Conditional-spread mirrors
  // the useMy fetch above so the default-partition path is unchanged.
  useRealtimeSubscription({
    patterns: [{ pattern: '#conversation#', matchType: 'prefix' }],
    ...(opts?.user !== undefined ? { user: opts.user } : {}),
    enabled: !!parentId,
    onUpdate: debouncedInvalidate,
    // Subscribe rejected → child list won't self-heal via WS. Invalidate once so
    // the 60s fallback poll isn't the only recovery path; no resubscribe.
    // Mirrors useGetConversations / useGetConversationById.
    onError: reason => {
      console.error('useGetChildConversations: subscribe rejected', {
        reason,
        parentId,
      });
      invalidate();
    },
  });

  // Sort a shallow copy — never mutate `useMy`'s memoized `data` array in place
  // (a plain `.sort()` would). Matches useGetConversations' fresh-copy sort.
  const sortedChildren = [...(data || [])].sort(
    (a: Conversation, b: Conversation) => {
      return (
        (new Date(b.created).getTime() || 0) -
        (new Date(a.created).getTime() || 0)
      );
    }
  );

  return { data: sortedChildren as Conversation[], invalidate, ...rest };
}

export function useGetConversationById(conversationId?: string) {
  const query = conversationId ? `#${conversationId}` : undefined;

  // ENG-4506: chat lives on the user partition (same instance useChatWorker
  // holds open). useWsLiveness observes only — it never opens a connection.
  const { wsConfigured, hasEverConnected } = useWsLiveness({ user: true });
  const statusPollMs =
    !wsConfigured || !hasEverConnected
      ? NO_WS_STATUS_POLL_MS
      : FALLBACK_POLL_MS;

  const { data, invalidate, ...rest } = useMy(
    { resource: 'conversation', query },
    {
      enabled: !!conversationId,
      refetchInterval: statusPollMs,
    }
  );

  const debouncedInvalidate = useDebouncedCallback(
    () => invalidate(),
    INVALIDATE_DEBOUNCE_MS,
    { maxWait: INVALIDATE_MAX_WAIT_MS }
  );

  // Scope the WS subscription to this specific conversation record.
  useRealtimeSubscription({
    patterns: conversationId
      ? [{ pattern: `#conversation#${conversationId}`, matchType: 'exact' }]
      : [],
    enabled: !!conversationId,
    onUpdate: debouncedInvalidate,
    // Subscribe rejected → this record won't self-heal via WS. Invalidate once
    // so the 60s fallback poll isn't the only recovery path; no resubscribe.
    onError: reason => {
      console.error('useGetConversationById: subscribe rejected', {
        reason,
        conversationId,
      });
      invalidate();
    },
  });

  const conversations = (data || []) as Conversation[];
  const conversation = conversations.find(
    c =>
      c.uuid === conversationId ||
      c.id === conversationId ||
      c.key?.includes(conversationId || '')
  );

  return {
    data: conversation as Conversation | undefined,
    invalidate,
    ...rest,
  };
}

export function useGetConversationTraces(traceId?: string) {
  const { data, isLoading, invalidate, ...rest } = useMy(
    { resource: 'event', query: traceId || '' },
    {
      enabled: !!traceId,
      refetchInterval: FALLBACK_POLL_MS,
    }
  );

  const debouncedInvalidate = useDebouncedCallback(
    () => invalidate(),
    INVALIDATE_DEBOUNCE_MS,
    { maxWait: INVALIDATE_MAX_WAIT_MS }
  );

  // Trace events are written to the account partition, so subscribe via
  // the account-scoped connection (user: false).
  useRealtimeSubscription({
    patterns: traceId
      ? [{ pattern: `#event#${traceId}`, matchType: 'prefix' }]
      : [],
    enabled: !!traceId,
    user: false,
    onUpdate: debouncedInvalidate,
  });

  const tracesArray = Array.isArray(data)
    ? data
    : (data as { traceevents?: TraceEvent[] })?.traceevents || [];

  return {
    data: tracesArray as TraceEvent[],
    isLoading,
    invalidate,
    ...rest,
  };
}
